<template>
  <section class="wiki-editor">
    <div class="editor-hero">
      <h1>{{ isEdit ? 'تعديل المقال' : 'مقال جديد' }}</h1>
      <p>اكتب المحتوى الغني، أضف صورًا وروابط، واجعل @الإشارة للموظفين سهلة.</p>
    </div>

    <form class="editor-form" @submit.prevent="handleSave">
      <div class="field">
        <label>العنوان</label>
        <InputText v-model="form.title" required />
      </div>

      <div class="field">
        <label>المسار (slug)</label>
        <InputText v-model="form.slug" placeholder="يُولد تلقائياً إن تُرك فارغاً" />
      </div>

      <div class="field">
        <label>الملخص</label>
        <Textarea v-model="form.summary" rows="3" autoResize />
      </div>

      <div class="field">
        <label>تاريخ الحدث</label>
        <input type="date" v-model="form.event_date" />
      </div>

      <div class="field">
        <label>المحتوى</label>
        <div class="editor-toolbar">
          <Button
            v-for="action in toolbarActions"
            :key="action.label"
            severity="secondary"
            outlined
            size="small"
            :title="action.label"
            :aria-label="action.label"
            @click.prevent="action.handler()"
          >
            <component :is="action.icon" class="toolbar-icon" aria-hidden="true" />
          </Button>
          <input
            ref="attachmentInput"
            class="hidden-attachment-input"
            type="file"
            accept="*/*"
            @change="handleAttachmentSelection"
          />
        </div>
        <EditorContent v-if="editor" :editor="editor" class="tiptap" />
        <div class="mentions-preview" v-if="mentionedEmployees.length">
          <h4>الموظفون المذكورون</h4>
          <ul>
            <li v-for="emp in mentionedEmployees" :key="emp.id">
              <RouterLink :to="`/employee/${emp.id}`">@{{ emp.label }}</RouterLink>
            </li>
          </ul>
        </div>
        <div class="attachments-preview" v-if="wikiAttachments.length">
          <h4>المرفقات الحالية</h4>
          <ul>
            <li v-for="attachment in wikiAttachments" :key="attachment.attachment_id" class="attachment-badge">
              <div class="attachment-info">
                <a
                  class="attachment-link"
                  :href="attachment.url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ attachment.file_name }}
                </a>
                <span class="attachment-type">{{ attachment.display_type }}</span>
              </div>
              <button
                type="button"
                class="attachment-delete"
                :title="'حذف ' + attachment.file_name"
                :disabled="deletingAttachmentId === attachment.attachment_id"
                @click="deleteAttachment(attachment)"
              >
                ×
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div class="actions">
        <Button type="submit" :label="isEdit ? 'حفظ التعديلات' : 'نشر المقال'" :loading="saving" />
        <Button type="button" label="إلغاء" text @click="cancel" />
      </div>
    </form>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import { useToast } from 'primevue/usetoast'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import type { Editor, Range } from '@tiptap/core'
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import {
  BoldIcon,
  ItalicIcon,
  StrikethroughIcon,
  ListBulletIcon,
  QueueListIcon,
  ChatBubbleLeftRightIcon,
  HashtagIcon,
  LinkIcon,
  PhotoIcon,
  SparklesIcon,
  PaperClipIcon,
} from '@heroicons/vue/24/outline'
import axios from 'axios'
import { useAuthStore } from '@/store/auth'
import {
  createWikiPage,
  updateWikiPage,
  fetchWikiPage,
  fetchHashtagSuggestions,
  createCustomTag,
  uploadWikiAttachment,
  deleteWikiAttachment,
  type WikiHashtagRef,
  type HashtagSuggestion,
  type WikiAttachmentRecord,
} from '@/services/wiki'
import 'prosemirror-view/style/prosemirror.css'
import '@/styles/tiptap.css'
import 'tippy.js/dist/tippy.css'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const toast = useToast()

const form = reactive({
  title: '',
  slug: '',
  summary: '',
  event_date: '',
})

const saving = ref(false)
const isEdit = computed(() => !!route.params.slug)
const mentionedEmployees = ref<{ id: number; label: string }[]>([])
const currentHashtags = ref<WikiHashtagRef[]>([])
const wikiAttachments = ref<WikiAttachmentRecord[]>([])
const attachmentInput = ref<HTMLInputElement | null>(null)
const uploadingAttachment = ref(false)
const deletingAttachmentId = ref<number | null>(null)
const canUploadAttachment = computed(() => isEdit.value && !!form.slug)

type EmployeeLookupItem = {
  id: number
  label: string
  employee_id: number
  company?: string | null
  department?: string | null
  location?: string | null
}

type SuggestionRendererOptions<T> = {
  getLabel: (item: T) => string
  getMeta?: (item: T) => string | null
}

const TAG_TYPE_LABELS: Record<string, string> = {
  company: 'شركة',
  department: 'قسم',
  rank: 'رتبة',
  office: 'مكتب',
  title: 'منصب',
  hierarchy: 'هيكل',
  city: 'مدينة',
  state: 'ولاية',
  country: 'دولة',
  tag: 'وسم',
}

function formatTagTypeLabel(type?: string | null) {
  if (!type) return 'وسم'
  return TAG_TYPE_LABELS[type] || 'وسم'
}

function formatEmployeeMeta(item: EmployeeLookupItem) {
  const parts = [item.company, item.department, item.location].filter(Boolean)
  return parts.length ? parts.join(' • ') : null
}

function createSuggestionRenderer<T>(options: SuggestionRendererOptions<T>) {
  let popup: TippyInstance | null = null
  let container: HTMLDivElement | null = null
  let props: SuggestionProps<T, any> | null = null
  let activeIndex = 0

  const updateActiveState = () => {
    const listEl = container
    if (!listEl) return
    const nodes = listEl.querySelectorAll('.wiki-suggestion-item')
    nodes.forEach((node, idx) => {
      node.classList.toggle('is-active', idx === activeIndex)
    })
  }

  const selectItem = (index: number) => {
    if (!props) return
    const item = props.items[index]
    if (item) {
      props.command(item)
    }
  }

  const renderList = () => {
    const listEl = container
    if (!listEl || !props) return
    listEl.innerHTML = ''
    const items = props.items || []
    if (!items.length) {
      const empty = document.createElement('div')
      empty.className = 'wiki-suggestion-empty'
      empty.textContent = 'لا توجد نتائج'
      listEl.appendChild(empty)
      return
    }

    items.forEach((item, idx) => {
      const option = document.createElement('button')
      option.type = 'button'
      option.className = 'wiki-suggestion-item'

      const title = document.createElement('span')
      title.className = 'label'
      title.textContent = options.getLabel(item)
      option.appendChild(title)

      const metaText = options.getMeta?.(item)
      if (metaText) {
        const meta = document.createElement('small')
        meta.textContent = metaText
        option.appendChild(meta)
      }

      option.addEventListener('mouseenter', () => {
        activeIndex = idx
        updateActiveState()
      })
      option.addEventListener('mousedown', (event) => {
        event.preventDefault()
        selectItem(idx)
      })

      listEl.appendChild(option)
    })
    updateActiveState()
  }

  return {
    onStart(newProps: SuggestionProps<T>) {
      props = newProps
      activeIndex = 0
      container = document.createElement('div')
      container.classList.add('wiki-suggestion-list')
      renderList()
      if (typeof document !== 'undefined') {
        popup = tippy(document.body, {
          getReferenceClientRect: () => newProps.clientRect?.() ?? new DOMRect(),
          appendTo: () => document.body,
          content: container,
          interactive: true,
          showOnCreate: true,
          trigger: 'manual',
          placement: 'bottom-start',
        })
      }
    },
    onUpdate(newProps: SuggestionProps<T>) {
      props = newProps
      if (newProps.items.length === 0) {
        activeIndex = 0
      } else if (activeIndex > newProps.items.length - 1) {
        activeIndex = newProps.items.length - 1
      }
      renderList()
      if (popup) {
        popup.setProps({
          getReferenceClientRect: () => newProps.clientRect?.() ?? new DOMRect(),
        })
      }
    },
    onKeyDown({ event }: SuggestionKeyDownProps) {
      if (!props || !props.items.length) {
        if (event.key === 'Escape') {
          popup?.hide()
          return true
        }
        return false
      }
      if (event.key === 'ArrowDown') {
        activeIndex = (activeIndex + 1) % props.items.length
        updateActiveState()
        return true
      }
      if (event.key === 'ArrowUp') {
        activeIndex = (activeIndex + props.items.length - 1) % props.items.length
        updateActiveState()
        return true
      }
      if (event.key === 'Enter') {
        selectItem(activeIndex)
        return true
      }
      if (event.key === 'Escape') {
        popup?.hide()
        return true
      }
      return false
    },
    onExit() {
      popup?.destroy()
      popup = null
      container?.remove()
      container = null
      props = null
      activeIndex = 0
    },
  }
}

function normalizeTagLabel(label: string) {
  return label.replace(/^#/, '').trim()
}

const mentionAllowedPrefixes = [' ', '\t', '\n', '(', '[', '{', '"', "'", '']

const employeeMentionExtension = Mention.extend({
  name: 'employeeMention',
  addAttributes() {
    return {
      id: {
        default: null,
      },
      label: {
        default: null,
      },
    }
  },
  renderHTML({ node }) {
    const label = node.attrs.label || node.attrs.id
    return [
      'a',
      {
        class: 'wiki-mention',
        href: `/employee/${node.attrs.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      `@${label}`,
    ]
  },
}).configure({
  suggestion: {
    char: '@',
    allowSpaces: true,
    allowedPrefixes: mentionAllowedPrefixes,
    items: async ({ query }: { query: string }) => {
      const term = query.trim()
      if (!term) return []
      try {
        const { data } = await axios.get('/api/employees/lookup', {
          params: { q: term, limit: 20 },
        })
        return data.map(
          (item: any): EmployeeLookupItem => ({
            ...item,
            id: item.employee_id,
            label:
              `${item.first_name || ''} ${item.last_name || ''}`.trim() || `#${item.employee_id}`,
            employee_id: item.employee_id,
          })
        )
      } catch (err) {
        console.error('Failed to load employee suggestions', err)
        return []
      }
    },
    command: ({ editor, range, props }) => {
      editor
        .chain()
        .focus()
        .insertContentAt({ from: range.from, to: range.to }, [
          { type: 'employeeMention', attrs: { id: props.id, label: props.label } },
        ])
        .run()
    },
    render: () => createSuggestionRenderer<EmployeeLookupItem>({
      getLabel: (item) => item.label,
      getMeta: (item) => formatEmployeeMeta(item),
    }),
  },
})

const hashtagMentionExtension = Mention.extend({
  name: 'wikiHashtag',
  addAttributes() {
    return {
      label: { default: null },
      type: { default: 'tag' },
      entityId: { default: null },
      tagId: { default: null },
      slug: { default: null },
    }
  },
  renderHTML({ node }) {
    const label = node.attrs.label || ''
    return ['span', { class: 'wiki-hashtag', 'data-type': node.attrs.type }, `#${label}`]
  },
}).configure({
  suggestion: {
    char: '#',
    allowSpaces: true,
    allowedPrefixes: mentionAllowedPrefixes,
    items: async ({ query }: { query: string }) => {
      const term = query.replace(/^#+/, '').trim()
      if (!term) return []
      try {
        return await fetchHashtagSuggestions(term)
      } catch (err) {
        console.error('Failed to load hashtag suggestions', err)
        return []
      }
    },
    command: ({ editor, range, props }: { editor: Editor; range: Range; props: any }) => {
      const data = props as HashtagSuggestion
      const insert = (finalProps: HashtagSuggestion) => {
        editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to: range.to }, [
            {
              type: 'wikiHashtag',
              attrs: {
                label: finalProps.label.replace(/^#/, ''),
                type: finalProps.type,
                entityId: finalProps.entity_id ?? null,
                tagId: finalProps.tag_id ?? null,
                slug: finalProps.slug ?? null,
              },
            },
          ])
          .run()
      }

      if (data.requires_creation) {
        createCustomTag(normalizeTagLabel(data.label))
          .then((created) => {
            insert({
              ...data,
              type: 'tag',
              tag_id: created.tag_id,
              slug: created.slug,
              requires_creation: false,
            })
          })
          .catch(() => {
            toast.add({ severity: 'error', summary: 'تعذر إنشاء الوسم', life: 2500 })
          })
        return
      }
      insert(props)
    },
    render: () => createSuggestionRenderer<HashtagSuggestion>({
      getLabel: (item) => `#${normalizeTagLabel(item.label)}`,
      getMeta: (item) => (item.requires_creation ? 'وسم جديد' : formatTagTypeLabel(item.type)),
    }),
  },
})

const editor = useEditor({
  extensions: [
    StarterKit,
    Link.configure({
      autolink: true,
      openOnClick: true,
      linkOnPaste: true,
    }),
    Image.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          style: {
            default:
              'max-width:none;width:auto;height:auto;resize:both;overflow:auto;cursor:se-resize;display:inline-block;',
            parseHTML: (element) => element.getAttribute('style') || '',
            renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
          },
        }
      },
    }).configure({
      inline: true,
      allowBase64: true,
    }),
    Placeholder.configure({
      includeChildren: true,
      placeholder: 'اكتب تفاصيل المقال هنا...',
    }),
    employeeMentionExtension,
    hashtagMentionExtension,
  ],
  content: '<p></p>',
})

onMounted(() => {
  if (!auth.isAdminRole) {
    router.replace('/wiki')
    return
  }
  if (isEdit.value) {
    loadPage()
  }
})

watch(editor, (instance, _, onCleanup) => {
  if (!instance) return
  const handler = () => refreshDerivedData()
  instance.on('update', handler)
  instance.on('create', handler)
  onCleanup(() => {
    instance.off('update', handler)
    instance.off('create', handler)
  })
})

watch(
  () => route.params.slug,
  () => {
    if (isEdit.value) loadPage()
  }
)

async function loadPage() {
  if (!route.params.slug) return
  const slug = route.params.slug as string
    try {
      const data = await fetchWikiPage(slug)
      form.title = data.title
      form.slug = data.slug
      form.summary = data.summary || ''
      form.event_date = data.event_date || ''
      wikiAttachments.value = data.wiki_attachments || []
      let payload = data.content_html
      if (data.content_json) {
      try {
        payload = JSON.parse(data.content_json)
      } catch (err) {
        console.warn('Failed to parse stored JSON content', err)
      }
    }
    editor?.value?.commands.setContent(payload || '<p></p>')
    refreshDerivedData()
  } catch (err) {
    toast.add({ severity: 'error', summary: 'فشل التحميل', life: 3000 })
  }
}

function collectNodesByType(node: any, typeName: string, acc: any[] = []) {
  if (!node) return acc
  if (node.type === typeName) {
    acc.push(node)
  }
  if (Array.isArray(node.content)) {
    node.content.forEach((child: any) => collectNodesByType(child, typeName, acc))
  }
  return acc
}

function collectMentionIds(doc: any) {
  const nodes = collectNodesByType(doc, 'employeeMention', [])
  const ids = new Set<number>()
  nodes.forEach((node: any) => {
    if (node.attrs?.id) ids.add(Number(node.attrs.id))
  })
  return ids
}

function collectHashtagRefs(doc: any): WikiHashtagRef[] {
  const nodes = collectNodesByType(doc, 'wikiHashtag', [])
  const seen = new Set<string>()
  const refs: WikiHashtagRef[] = []
  nodes.forEach((node: any) => {
    const attrs = node.attrs || {}
    const key = `${attrs.type}-${attrs.entityId ?? ''}-${attrs.tagId ?? ''}-${attrs.label ?? ''}`
    if (seen.has(key)) return
    seen.add(key)
    refs.push({
      label: attrs.label || '',
      type: attrs.type || 'tag',
      slug: attrs.slug || undefined,
      entity_id: attrs.entityId ?? undefined,
      tag_id: attrs.tagId ?? undefined,
    })
  })
  return refs
}

function refreshDerivedData() {
  if (!editor.value) return
  const doc = editor.value.getJSON()
  const mentionNodes = collectNodesByType(doc, 'employeeMention', [])
  const unique = new Map<number, { id: number; label: string }>()
  mentionNodes.forEach((node: any) => {
    const id = Number(node.attrs?.id)
    if (!id) return
    if (!unique.has(id)) {
      unique.set(id, { id, label: node.attrs?.label || `#${id}` })
    }
  })
  mentionedEmployees.value = Array.from(unique.values())
  currentHashtags.value = collectHashtagRefs(doc)
}

async function handleSave() {
  if (!editor?.value) return
  saving.value = true
  try {
    const doc = editor.value.getJSON()
    const linked = Array.from(collectMentionIds(doc))
    const hashtags = currentHashtags.value
    const payload = {
      title: form.title,
      slug: form.slug || undefined,
      summary: form.summary || undefined,
      event_date: form.event_date || undefined,
      content_html: editor.value.getHTML(),
      content_json: JSON.stringify(doc),
      linked_employee_ids: linked,
      hashtags,
    }
    const slug = route.params.slug as string
    if (isEdit.value && slug) {
      const updated = await updateWikiPage(slug, payload)
      toast.add({ severity: 'success', summary: 'تم الحفظ', life: 2000 })
      router.push(`/wiki/${updated.slug}`)
    } else {
      const created = await createWikiPage(payload)
      toast.add({ severity: 'success', summary: 'تم النشر', life: 2000 })
      router.push(`/wiki/${created.slug}`)
    }
  } catch (err: any) {
    toast.add({
      severity: 'error',
      summary: 'فشل الحفظ',
      detail: err?.response?.data?.detail || 'تعذر حفظ المقال',
      life: 4000,
    })
  } finally {
    saving.value = false
  }
}

const toolbarActions = [
  { label: 'عريض', icon: BoldIcon, handler: () => toggleBold() },
  { label: 'مائل', icon: ItalicIcon, handler: () => toggleItalic() },
  { label: 'تشطيب', icon: StrikethroughIcon, handler: () => toggleStrike() },
  { label: 'قائمة نقطية', icon: ListBulletIcon, handler: () => toggleBullet() },
  { label: 'قائمة مرقمة', icon: QueueListIcon, handler: () => toggleOrderedList() },
  { label: 'اقتباس', icon: ChatBubbleLeftRightIcon, handler: () => toggleQuote() },
  { label: 'عنوان فرعي', icon: HashtagIcon, handler: () => setHeadingLevel(2) },
  { label: 'رابط', icon: LinkIcon, handler: () => setLink() },
  { label: 'صورة', icon: PhotoIcon, handler: () => insertImage() },
  { label: 'مرفق', icon: PaperClipIcon, handler: () => triggerAttachmentUpload() },
  { label: 'إزالة التنسيقات', icon: SparklesIcon, handler: () => clearFormatting() },
]

function toggleBold() {
  editor?.value?.chain().focus().toggleBold().run()
}
function toggleItalic() {
  editor?.value?.chain().focus().toggleItalic().run()
}
function toggleStrike() {
  editor?.value?.chain().focus().toggleStrike().run()
}
function toggleBullet() {
  editor?.value?.chain().focus().toggleBulletList().run()
}
function toggleOrderedList() {
  editor?.value?.chain().focus().toggleOrderedList().run()
}
function toggleQuote() {
  editor?.value?.chain().focus().toggleBlockquote().run()
}
function setHeadingLevel(level: 1 | 2 | 3 | 4 | 5 | 6) {
  editor?.value?.chain().focus().toggleHeading({ level }).run()
}
function setLink() {
  const url = prompt('أدخل الرابط')
  if (url) editor?.value?.chain().focus().setLink({ href: url }).run()
}
function insertImage() {
  const url = prompt('أدخل رابط الصورة')
  if (url) editor?.value?.chain().focus().setImage({ src: url }).run()
}
function triggerAttachmentUpload() {
  if (!canUploadAttachment.value) {
    toast.add({
      severity: 'warn',
      summary: 'يرجى حفظ المقال أولاً',
      detail: 'يجب أن تكون المقالة محفوظة لرفع المرفقات',
      life: 3000,
    })
    return
  }
  attachmentInput.value?.click()
}

async function handleAttachmentSelection() {
  const files = attachmentInput.value?.files
  if (!files || !files.length) return
    const file = files[0]
    attachmentInput.value!.value = ''
    await uploadAttachmentFile(file)
}

async function uploadAttachmentFile(file: File) {
  if (!form.slug) {
    toast.add({
      severity: 'error',
      summary: 'تعذر رفع المرفق',
      detail: 'يجب حفظ المقال أولاً لإنشاء مسار ثابت',
      life: 3500,
    })
    return
  }
  uploadingAttachment.value = true
  try {
    const attachment = await uploadWikiAttachment(form.slug, file)
    addOrReplaceAttachment(attachment)
    try {
      insertAttachmentMarkup(attachment)
    } catch (err) {
      console.error('Failed to insert attachment markup', err)
    }
    toast.add({
      severity: 'success',
      summary: 'تم إضافة المرفق',
      life: 2500,
    })
  } catch (err: any) {
    toast.add({
      severity: 'error',
      summary: 'فشل رفع المرفق',
      detail: err?.response?.data?.detail || 'تعذر رفع الملف',
      life: 4000,
    })
  } finally {
    uploadingAttachment.value = false
  }
}

function addOrReplaceAttachment(attachment: WikiAttachmentRecord) {
  const existing = wikiAttachments.value.find((item) => item.attachment_id === attachment.attachment_id)
  if (existing) {
    wikiAttachments.value = wikiAttachments.value.map((item) =>
      item.attachment_id === attachment.attachment_id ? attachment : item
    )
  } else {
    wikiAttachments.value = [...wikiAttachments.value, attachment]
  }
}

async function deleteAttachment(attachment: WikiAttachmentRecord) {
  if (!form.slug) return
  deletingAttachmentId.value = attachment.attachment_id
  try {
    await deleteWikiAttachment(form.slug, attachment.attachment_id)
    wikiAttachments.value = wikiAttachments.value.filter((item) => item.attachment_id !== attachment.attachment_id)
    removeAttachmentFromContent(attachment)
    toast.add({ severity: 'success', summary: 'تم حذف المرفق', life: 2500 })
  } catch (err: any) {
    toast.add({
      severity: 'error',
      summary: 'فشل الحذف',
      detail: err?.response?.data?.detail || 'تعذر حذف المرفق',
      life: 3000,
    })
  } finally {
    deletingAttachmentId.value = null
  }
}

function escapeHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function insertAttachmentMarkup(attachment: WikiAttachmentRecord) {
  if (!editor?.value) return
  const caption = escapeHtml(attachment.file_name)
  let mediaHtml = ''
  const source = attachment.url
  const chain = editor.value.chain().focus()
  if (attachment.display_type === 'image') {
    chain.setImage({ src: source, alt: caption }).run()
    chain.updateAttributes('image', {
      style:
        'max-width:none;width:auto;height:auto;resize:both;overflow:auto;cursor:se-resize;display:inline-block;',
    }).run()
    chain.insertContent(`<p class="wiki-attachment-caption">${caption}</p>`).run()
  } else {
    const linkHtml = `<p><a href="${source}" target="_blank" rel="noopener noreferrer">${caption}</a></p>`
    chain.insertContent(linkHtml).run()
  }
}

function removeAttachmentFromContent(attachment: WikiAttachmentRecord) {
  if (!editor?.value) return
  const doc = editor.value.getJSON()
  if (!doc?.content) return
  const filtered = filterContentNodes(doc.content, attachment)
  if (JSON.stringify(filtered) === JSON.stringify(doc.content)) return
  const updatedDoc = { ...doc, content: filtered }
  editor.value.chain().focus().setContent(updatedDoc).run()
}

function filterContentNodes(nodes: any[], attachment: WikiAttachmentRecord): any[] {
  const result: any[] = []
  nodes.forEach((node) => {
    if (isAttachmentNode(node, attachment)) return
    let updatedNode = node
    if (Array.isArray(node.content) && node.content.length) {
      const nested = filterContentNodes(node.content, attachment)
      if (nested.length !== node.content.length) {
        updatedNode = { ...node, content: nested }
      }
    }
    result.push(updatedNode)
  })
  return result
}

function isAttachmentNode(node: any, attachment: WikiAttachmentRecord) {
  if (!node) return false
  if (node.type === 'image' && node.attrs?.src === attachment.url) {
    return true
  }
  if (node.type === 'paragraph') {
    if (node.attrs?.class === 'wiki-attachment-caption') {
      return true
    }
    if (Array.isArray(node.content) && node.content.some((child: any) => isAttachmentLink(child, attachment.url))) {
      return true
    }
  }
  return false
}

function isAttachmentLink(node: any, url: string) {
  if (!node) return false
  if (node.marks?.some((mark: any) => mark.type === 'link' && mark.attrs?.href === url)) {
    return true
  }
  if (node.attrs?.href === url) {
    return true
  }
  return false
}
function clearFormatting() {
  editor?.value?.chain().focus().unsetAllMarks().clearNodes().run()
}

function cancel() {
  router.back()
}
</script>

<style scoped>
.wiki-editor {
  max-width: 900px;
  margin: 0 auto;
  padding: 5rem 1rem 2rem;
}

@media (max-width: 768px) {
  .wiki-editor {
    padding-top: 4rem;
  }
}

.editor-hero {
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 15px 35px rgba(15, 23, 42, 0.08);
}

.editor-hero h1 {
  margin: 0 0 0.25rem;
  color: #0f172a;
}

.editor-hero p {
  margin: 0;
  color: #475569;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.hidden-attachment-input {
  display: none;
}

.attachments-preview {
  margin-top: 1rem;
}

.attachments-preview ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.attachment-badge {
  background: #eef2ff;
  border-radius: 0.75rem;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  color: #4338ca;
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
  position: relative;
}

.attachment-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.attachment-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
}

.attachment-link:hover {
  text-decoration: underline;
}

.attachment-delete {
  border: none;
  background: transparent;
  color: #dc2626;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.attachment-delete:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.editor-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.editor-toolbar {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.editor-toolbar :deep(.p-button) {
  background: #f8fafc;
  border: 1px solid #cbd5f5;
  color: #0f172a;
}

.toolbar-icon {
  width: 1rem;
  height: 1rem;
}

.editor-toolbar :deep(.p-button:hover) {
  background: #e2e8ff;
  border-color: #94a3f4;
  color: #1e1b4b;
}

.tiptap {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem;
  min-height: 280px;
}

.tiptap :global(p) {
  margin: 0 0 1rem;
}

.wiki-mention {
  background: #eef2ff;
  border-radius: 4px;
  padding: 0 4px;
}

.wiki-hashtag {
  background: #ecfeff;
  border-radius: 4px;
  padding: 0 4px;
}

.mentions-preview {
  margin-top: 1rem;
  border: 1px dashed #cbd5f5;
  border-radius: 0.5rem;
  padding: 0.75rem;
  background: #f8fafc;
}

.mentions-preview ul {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.mentions-preview a {
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 1rem;
}
</style>
